<template>
  <div class="max-w-7xl mx-auto px-4 py-12 flex flex-col lg:flex-row gap-8 animate-fade-in font-sans">

    <!-- SIDEBAR -->
    <aside class="w-full lg:w-1/4 space-y-8">

      <!-- Search -->
      <div class="relative group">
        <input
          v-model="searchQuery"
          type="text"
          placeholder="Caută produs..."
          class="w-full pl-10 pr-4 py-3 rounded-2xl border-2 border-gray-100 bg-white focus:border-carpatina-portocaliu outline-none font-bold text-sm transition shadow-sm group-hover:shadow-md"
        />
        <Search :size="18" class="absolute left-3 top-3.5 text-gray-400 group-focus-within:text-carpatina-portocaliu transition" />
      </div>

      <!-- Categorii -->
      <div class="bg-white rounded-3xl shadow-xl border-t-8 border-carpatina-verde overflow-hidden">
        <div class="p-6 pb-4 border-b border-gray-100">
          <h3 class="font-black uppercase text-carpatina-gri flex items-center gap-2 tracking-widest text-sm">
            <LayoutGrid :size="18" class="text-carpatina-portocaliu" /> Categorii
          </h3>
        </div>

        <div class="p-4 space-y-1">
          <button
            @click="selectCategory(null)"
            :class="selectedCat === null ? 'bg-carpatina-portocaliu text-white shadow-md scale-[1.02]' : 'text-gray-500 hover:bg-gray-50'"
            class="w-full text-left text-xs font-black py-3 px-4 rounded-xl transition-all duration-300 uppercase flex items-center gap-3 mb-2"
          >
            <LayoutList :size="16" /> Toate Produsele
          </button>

          <div v-for="cat in visibleCategories" :key="cat.id">
            <div
              class="flex items-center justify-between rounded-xl transition-all duration-200 cursor-pointer"
              :class="[
                cat.level === 0 ? 'py-3 px-3 mt-1 border-b border-gray-50 hover:bg-gray-50' : 'py-2 pr-2 hover:bg-orange-50/50',
                selectedCat === cat.id && cat.level === 0 ? 'bg-green-50' : '',
                selectedCat === cat.id && cat.level > 0 ? 'text-carpatina-portocaliu font-bold' : ''
              ]"
              :style="{ paddingLeft: cat.level === 0 ? '12px' : (cat.level * 20 + 12) + 'px' }"
              @click="selectCategory(cat.id)"
            >
              <div class="flex items-center gap-3 flex-1">
                <div v-if="cat.level === 0" :class="selectedCat === cat.id ? 'text-carpatina-verde' : 'text-gray-300'" class="transition-colors">
                  <Package :size="18" />
                </div>
                <div v-else class="w-1.5 h-1.5 rounded-full flex-shrink-0"
                  :class="selectedCat === cat.id ? 'bg-carpatina-portocaliu' : 'bg-gray-300'">
                </div>
                <span
                  :class="[
                    cat.level === 0 ? 'uppercase font-black text-xs tracking-wider text-carpatina-gri' : 'text-sm text-gray-500 font-medium',
                    selectedCat === cat.id && cat.level === 0 ? '!text-carpatina-verde' : ''
                  ]"
                  class="transition-colors leading-tight"
                >
                  {{ cat.nume }}
                </span>
              </div>

              <button
                v-if="hasChildren(cat.id)"
                @click.stop="toggleExpand(cat.id)"
                class="p-1 rounded-full hover:bg-white hover:shadow-sm transition text-gray-400 flex-shrink-0"
              >
                <ChevronDown
                  :size="14"
                  :class="expandedCats.includes(cat.id) ? 'rotate-180 text-carpatina-portocaliu' : ''"
                  class="transition-transform duration-300"
                />
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Filtru Pret -->
      <div class="bg-carpatina-gri p-6 rounded-3xl shadow-xl text-white relative overflow-hidden">
        <div class="absolute top-0 right-0 p-4 opacity-10 transform rotate-12 pointer-events-none">
          <Banknote :size="64" />
        </div>
        <h3 class="font-black uppercase text-xs mb-4 tracking-widest text-carpatina-portocaliu flex items-center gap-2 relative z-10">
          <Banknote :size="16" /> Interval Preț (RON)
        </h3>
        <div class="flex gap-2 items-center text-carpatina-gri relative z-10">
          <input v-model.number="minPrice" type="number" placeholder="Min"
            class="w-full p-2.5 rounded-xl text-xs font-bold outline-none border-2 border-transparent focus:border-carpatina-portocaliu text-center shadow-inner" />
          <span class="text-white font-bold opacity-50">-</span>
          <input v-model.number="maxPrice" type="number" placeholder="Max"
            class="w-full p-2.5 rounded-xl text-xs font-bold outline-none border-2 border-transparent focus:border-carpatina-portocaliu text-center shadow-inner" />
        </div>
      </div>

    </aside>

    <!-- MAIN -->
    <main class="w-full lg:w-3/4 flex flex-col">

      <!-- Bara de sus: rezultate + per pagina + sortare -->
      <div class="flex flex-col sm:flex-row justify-between items-center mb-6 px-2 gap-4">
        <span class="text-xs font-bold text-gray-400 uppercase">
          Afișare <span class="text-carpatina-verde">{{ paginatedProducts.length }}</span> din {{ produseFiltrate.length }} rezultate
        </span>

        <div class="flex items-center gap-3 flex-wrap justify-end">

          <!-- Per pagina -->
          <div class="flex items-center gap-2">
            <span class="text-xs font-bold text-gray-400 uppercase hidden sm:inline">Pe pagină:</span>
            <div class="relative">
              <select
                v-model.number="itemsPerPage"
                class="appearance-none bg-white border-2 border-gray-100 pl-3 pr-8 py-2 rounded-xl text-xs font-bold text-carpatina-gri focus:border-carpatina-portocaliu outline-none cursor-pointer shadow-sm hover:shadow-md transition-all"
              >
                <option :value="6">6</option>
                <option :value="9">9</option>
                <option :value="12">12</option>
                <option :value="24">24</option>
              </select>
              <div class="absolute right-2 top-2.5 pointer-events-none text-gray-400">
                <ChevronDown :size="14" />
              </div>
            </div>
          </div>

          <!-- Sortare -->
          <div class="flex items-center gap-2">
            <span class="text-xs font-bold text-gray-400 uppercase hidden sm:inline">Ordonează:</span>
            <div class="relative">
              <select
                v-model="sortBy"
                class="appearance-none bg-white border-2 border-gray-100 pl-4 pr-10 py-2 rounded-xl text-xs font-bold text-carpatina-gri focus:border-carpatina-portocaliu outline-none cursor-pointer shadow-sm hover:shadow-md transition-all"
              >
                <option value="default">Implicit</option>
                <option value="random">Aleatoriu</option>
                <option value="nume_asc">Alfabetic (A-Z)</option>
                <option value="nume_desc">Alfabetic (Z-A)</option>
                <option value="pret_asc">Preț (Crescător)</option>
                <option value="pret_desc">Preț (Descrescător)</option>
              </select>
              <div class="absolute right-3 top-2.5 pointer-events-none text-gray-400">
                <ArrowUpDown :size="14" />
              </div>
            </div>
          </div>

        </div>
      </div>

      <!-- Grid produse -->
      <div v-if="paginatedProducts.length" class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
        <div
          v-for="p in paginatedProducts"
          :key="p.id"
          class="bg-white rounded-[2rem] shadow-lg overflow-hidden border-2 border-transparent hover:border-carpatina-verde transition-all group flex flex-col"
        >
          <div class="h-56 bg-gray-100 overflow-hidden relative">
            <img
              v-if="p.poze?.length"
              :src="'https://carpatinaprodcom.ro/api' + p.poze[0]?.url"
              class="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
            />
            <div v-else class="w-full h-full flex flex-col items-center justify-center text-gray-300 italic bg-gray-50">
              <ImageOff :size="32" />
              <span class="text-[10px] font-bold mt-2">Fără imagine</span>
            </div>
            <div
              :class="p.stoc ? 'bg-carpatina-verde' : 'bg-red-500'"
              class="absolute top-4 left-4 text-[10px] font-black text-white px-3 py-1 rounded-full uppercase italic shadow-md"
            >
              {{ p.stoc ? 'În Stoc' : 'La Comandă' }}
            </div>
          </div>

          <div class="p-6 space-y-3 flex flex-col flex-1">
            <h4 class="font-black text-carpatina-gri uppercase text-lg leading-tight">{{ p.nume }}</h4>

            <div class="text-[10px] font-black uppercase text-carpatina-portocaliu bg-orange-50 w-max px-2 py-1 rounded-md">
              {{ getCategoryName(p.categorie_id) }}
            </div>

            <div class="text-xs text-gray-400 font-bold italic line-clamp-3" style="min-height: 3em;">
              <span v-html="p.detalii"></span>
            </div>

            <div class="pt-4 flex items-center justify-between border-t border-gray-50 mt-auto">
              <div>
                <div v-if="p.pret" class="text-carpatina-verde font-black text-xl italic">
                  {{ p.pret }} <span class="text-xs uppercase not-italic text-gray-400">RON</span>
                </div>
                <div v-else class="text-carpatina-portocaliu font-black text-xs uppercase italic tracking-tighter">
                  Cere Preț
                </div>
              </div>

              <div class="flex items-center gap-2">
                <button
                  @click.prevent="adaugaInLista(p.id)"
                  :class="produsAdaugat === p.id ? 'bg-carpatina-verde text-white' : 'bg-carpatina-portocaliu text-white hover:bg-orange-600'"
                  class="w-10 h-10 flex items-center justify-center rounded-xl transition-all duration-300 shadow-md active:scale-95"
                  title="Adaugă în lista de ofertă"
                >
                  <Check v-if="produsAdaugat === p.id" :size="20" class="animate-pulse" />
                  <ShoppingCart v-else :size="20" />
                </button>

                <router-link
                  :to="{ path: '/produs/' + p.id, query: route.query }"
                  class="w-10 h-10 flex items-center justify-center bg-carpatina-gri text-white rounded-xl hover:bg-carpatina-verde transition-all shadow-md active:scale-95"
                  title="Vezi detalii"
                >
                  <ArrowUpRight :size="20" />
                </router-link>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Niciun produs -->
      <div v-else class="flex flex-col items-center justify-center text-center py-20 bg-white rounded-[3rem] shadow-inner opacity-60">
        <PackageSearch :size="64" class="text-gray-300 mb-4" />
        <p class="text-xl font-black uppercase italic text-gray-400">Niciun produs găsit</p>
        <button @click="resetFilters" class="mt-4 text-xs font-bold text-carpatina-portocaliu hover:underline">
          Resetează filtrele
        </button>
      </div>

      <!-- Paginare -->
      <div v-if="totalPages > 1" class="flex justify-center items-center gap-2 mt-6">

        <button
          @click="changePage(currentPage - 1)"
          :disabled="currentPage === 1"
          class="p-2 rounded-xl border-2 border-gray-100 hover:border-carpatina-portocaliu disabled:opacity-30 disabled:hover:border-gray-100 transition"
        >
          <ChevronLeft :size="20" class="text-carpatina-gri" />
        </button>

        <div class="flex gap-1 bg-white px-2 py-2 rounded-2xl shadow-sm border border-gray-100">
          <template v-for="(page, index) in visiblePages" :key="index">
            <span v-if="page === '...'" class="w-8 h-8 flex items-center justify-center text-gray-400 font-bold select-none">
              ...
            </span>
            <button
              v-else
              @click="changePage(page)"
              :class="currentPage === page ? 'bg-carpatina-portocaliu text-white shadow-lg scale-110' : 'text-gray-400 hover:text-carpatina-gri hover:bg-gray-50'"
              class="w-8 h-8 rounded-lg font-black text-xs transition flex items-center justify-center"
            >
              {{ page }}
            </button>
          </template>
        </div>

        <button
          @click="changePage(currentPage + 1)"
          :disabled="currentPage === totalPages"
          class="p-2 rounded-xl border-2 border-gray-100 hover:border-carpatina-portocaliu disabled:opacity-30 disabled:hover:border-gray-100 transition"
        >
          <ChevronRight :size="20" class="text-carpatina-gri" />
        </button>

      </div>

    </main>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, inject } from 'vue';
import axios from 'axios';
import {
  LayoutGrid, LayoutList, Package, PackageSearch,
  Search, Banknote, ImageOff, ArrowUpRight, ArrowUpDown,
  ChevronDown, ChevronLeft, ChevronRight,
  Check, ShoppingCart
} from 'lucide-vue-next';
import { useRoute, useRouter } from 'vue-router';

const route = useRoute();
const router = useRouter();
const cart = inject('cart');

// --- STATE ---
const produse = ref([]);
const categoriiRaw = ref([]);
const categoriiList = ref([]);
const selectedCat = ref(null);
const expandedCats = ref([]);
const searchQuery = ref('');
const minPrice = ref(null);
const maxPrice = ref(null);
const sortBy = ref('default');
const currentPage = ref(1);
const itemsPerPage = ref(9);
const produsAdaugat = ref(null);

// --- URL SYNC ---
const initFiltersFromUrl = () => {
  const q = route.query;
  if (q.page)  currentPage.value   = parseInt(q.page);
  if (q.q)     searchQuery.value   = q.q;
  if (q.min)   minPrice.value      = parseInt(q.min);
  if (q.max)   maxPrice.value      = parseInt(q.max);
  if (q.sort)  sortBy.value        = q.sort;
  if (q.ipp)   itemsPerPage.value  = parseInt(q.ipp);
  if (q.cat)   selectedCat.value   = parseInt(q.cat);
};

const updateUrl = () => {
  router.replace({
    query: {
      ...route.query,
      page:  currentPage.value > 1                  ? currentPage.value    : undefined,
      q:     searchQuery.value                      || undefined,
      cat:   selectedCat.value                      || undefined,
      min:   minPrice.value                         || undefined,
      max:   maxPrice.value                         || undefined,
      sort:  sortBy.value !== 'default'             ? sortBy.value         : undefined,
      ipp:   itemsPerPage.value !== 9               ? itemsPerPage.value   : undefined,
    }
  });
};

// --- CATEGORII ---
const buildHierarchy = (rawList) => {
  const sorted = [];
  const traverse = (parentId, level) => {
    const children = rawList
      .filter(c => c.parent_id === parentId)
      .sort((a, b) => a.nume.localeCompare(b.nume));
    children.forEach(child => {
      child.level = level;
      sorted.push(child);
      traverse(child.id, level + 1);
    });
  };
  traverse(null, 0);
  return sorted;
};

const hasChildren = (id) => categoriiRaw.value.some(c => c.parent_id === id);

const visibleCategories = computed(() =>
  categoriiList.value.filter(cat =>
    cat.level === 0 || expandedCats.value.includes(cat.parent_id)
  )
);

const getAllDescendantIds = (rootId) => {
  let ids = [rootId];
  categoriiRaw.value
    .filter(c => c.parent_id === rootId)
    .forEach(child => { ids = ids.concat(getAllDescendantIds(child.id)); });
  return ids;
};

const getCategoryName = (id) => {
  const c = categoriiRaw.value.find(x => x.id === id);
  return c ? c.nume : 'Necunoscut';
};

const toggleExpand = (catId) => {
  expandedCats.value = expandedCats.value.includes(catId)
    ? expandedCats.value.filter(id => id !== catId)
    : [...expandedCats.value, catId];
};

const selectCategory = (id) => {
  selectedCat.value = id;
  currentPage.value = 1;
  if (id && hasChildren(id) && !expandedCats.value.includes(id)) {
    expandedCats.value.push(id);
  }
};

const expandParents = (catId) => {
  let cat = categoriiRaw.value.find(c => c.id === catId);
  while (cat && cat.parent_id) {
    if (!expandedCats.value.includes(cat.parent_id)) {
      expandedCats.value.push(cat.parent_id);
    }
    cat = categoriiRaw.value.find(c => c.id === cat.parent_id);
  }
};

// --- DATE ---
const fetchData = async () => {
  try {
    initFiltersFromUrl();
    const [resP, resC] = await Promise.all([
      axios.get('/produse'),
      axios.get('/categorii'),
    ]);
    produse.value = resP.data;
    categoriiRaw.value = resC.data;
    categoriiList.value = buildHierarchy(resC.data);

    if (selectedCat.value) {
      if (hasChildren(selectedCat.value)) expandedCats.value.push(selectedCat.value);
      expandParents(selectedCat.value);
    }

    document.title = 'Catalog Materiale | Carpatina Prodcom';
  } catch (err) {
    console.error('Eroare date', err);
  }
};

// --- PRODUSE FILTRATE & SORTATE ---
const produseFiltrate = computed(() => {
  let result = produse.value.filter(p => {
    const matchCat = !selectedCat.value || getAllDescendantIds(selectedCat.value).includes(p.categorie_id);
    const matchMin = !minPrice.value || (p.pret && p.pret >= minPrice.value);
    const matchMax = !maxPrice.value || (p.pret && p.pret <= maxPrice.value);
    const q = searchQuery.value.toLowerCase();
    const matchSearch = !q || p.nume.toLowerCase().includes(q) || (p.detalii && p.detalii.toLowerCase().includes(q));
    return matchCat && matchMin && matchMax && matchSearch;
  });

  switch (sortBy.value) {
    case 'nume_asc':  return [...result].sort((a, b) => a.nume.localeCompare(b.nume));
    case 'nume_desc': return [...result].sort((a, b) => b.nume.localeCompare(a.nume));
    case 'pret_asc':  return [...result].sort((a, b) => (a.pret || 0) - (b.pret || 0));
    case 'pret_desc': return [...result].sort((a, b) => (b.pret || 0) - (a.pret || 0));
    case 'random':    return [...result].sort(() => Math.random() - 0.5);
    default:          return result;
  }
});

// --- PAGINARE ---
const totalPages = computed(() => Math.ceil(produseFiltrate.value.length / itemsPerPage.value));

const paginatedProducts = computed(() => {
  const start = (currentPage.value - 1) * itemsPerPage.value;
  return produseFiltrate.value.slice(start, start + itemsPerPage.value);
});

const changePage = (newPage) => {
  if (newPage >= 1 && newPage <= totalPages.value) {
    currentPage.value = newPage;
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }
};

const visiblePages = computed(() => {
  const total = totalPages.value;
  const current = currentPage.value;
  const delta = 1;
  const range = [];
  for (let i = 1; i <= total; i++) {
    if (i === 1 || i === total || (i >= current - delta && i <= current + delta)) {
      range.push(i);
    }
  }
  const result = [];
  let prev;
  for (const i of range) {
    if (prev) {
      if (i - prev === 2) result.push(prev + 1);
      else if (i - prev > 2) result.push('...');
    }
    result.push(i);
    prev = i;
  }
  return result;
});

// --- CART ---
const adaugaInLista = (id) => {
  cart.add(id, 1);
  produsAdaugat.value = id;
  setTimeout(() => {
    if (produsAdaugat.value === id) produsAdaugat.value = null;
  }, 1500);
};

// --- RESET ---
const resetFilters = () => {
  selectedCat.value = null;
  minPrice.value = null;
  maxPrice.value = null;
  searchQuery.value = '';
  sortBy.value = 'default';
  itemsPerPage.value = 9;
};

// --- WATCHERS ---
watch([minPrice, maxPrice, searchQuery, selectedCat, itemsPerPage], () => {
  currentPage.value = 1;
});

watch([currentPage, searchQuery, selectedCat, minPrice, maxPrice, sortBy, itemsPerPage], () => {
  updateUrl();
});

onMounted(fetchData);
</script>

<style scoped>
.animate-fade-in {
  animation: fadeIn 0.5s ease-out;
}
@keyframes fadeIn {
  from { opacity: 0; transform: translateY(20px); }
  to   { opacity: 1; transform: translateY(0); }
}
</style>